import React, { memo, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { format, isBefore, startOfDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import type { DoctorShift } from '../../../../../types/doctorShift';
import {
  TimelineContainer,
  EmptyState,
  DateGroup,
  DoctorRow,
  DateLabelColumn,
  DoctorRowsContainer,
  ShiftArea,
} from '../../MonthlyDoctorsShift.styles';
import { TimelineHeader } from './TimelineHeader';
import { ShiftBar } from './ShiftBar';
import { useTimelinePosition } from '../../hooks/useTimelinePosition';
import { extractTimeHm } from '../../../../../utils/shiftTime';

/**
 * 結合シフトセグメント
 *
 * 枠単位（1時間行）で保存されたシフトのうち、同一医師・同日で
 * 時間が連続しているものを表示上1本のバーにまとめたもの。
 */
interface MergedShiftSegment {
  /** 表示用キー（先頭シフトのIDから生成） */
  key: string;
  /** 代表シフト（先頭行）。クリック時にはこの行を渡す */
  shift: DoctorShift;
  /** 結合後の開始時刻（HH:mm） */
  startTime: string;
  /** 結合後の終了時刻（HH:mm） */
  endTime: string;
  /** 結合された行数 */
  shiftCount: number;
}

/**
 * TimelineView Props
 */
interface TimelineViewProps {
  /** 表示するシフト一覧 */
  shifts: DoctorShift[];
  /** 表示対象日付の配列 */
  dates: Date[];
  /** シフトバークリックハンドラー（結合後の表示時間帯も渡す） */
  onShiftClick: (
    shift: DoctorShift,
    timeRange: { startTime: string; endTime: string }
  ) => void;
  /** タイムライン空き領域ダブルクリックハンドラー */
  onDoubleClick: (date: string, time: string) => void;
}

/**
 * タイムラインビューコンポーネント
 *
 * @description
 * 医師シフトをガントチャート形式で表示するメインコンポーネント。
 * 07:00〜22:00の時間軸を横方向に、各日付を縦方向に配置。
 *
 * @param {TimelineViewProps} props - コンポーネントProps
 * @returns {React.ReactElement} タイムラインビュー
 *
 * @example
 * <TimelineView
 *   shifts={shifts}
 *   dates={datesInRange}
 *   onShiftClick={handleShiftClick}
 *   onDoubleClick={handleDoubleClick}
 * />
 */
const TimelineView: React.FC<TimelineViewProps> = memo(
  ({ shifts, dates, onShiftClick, onDoubleClick }) => {
    const { calculatePosition, config } = useTimelinePosition();

    // 過去日付かどうか判定
    const isPastDate = useCallback((date: Date) => {
      return isBefore(date, startOfDay(new Date()));
    }, []);

    // 日付→医師の2段階でグループ化し、同一医師の連続シフトは表示上1本に結合する。
    // （シフトは募集枠単位の1時間行で保存されるため、そのまま描画すると
    //   連続勤務が1時間ごとに分かれて見える）
    const segmentsByDateAndDoctor = useMemo(() => {
      const grouped = new Map<string, Map<string, DoctorShift[]>>();

      // 日付ごとの初期化
      dates.forEach((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        grouped.set(dateStr, new Map<string, DoctorShift[]>());
      });

      // シフトを日付→医師でグループ化
      shifts.forEach((shift) => {
        const dateMap = grouped.get(shift.shift_date);
        if (dateMap) {
          const doctorId = shift.doctor_id;
          const existing = dateMap.get(doctorId) || [];
          dateMap.set(doctorId, [...existing, shift]);
        }
      });

      // 医師ごとに開始時刻順へ並べ、終了時刻=次の開始時刻なら結合する
      const merged = new Map<string, Map<string, MergedShiftSegment[]>>();
      grouped.forEach((dateMap, dateStr) => {
        const doctorSegments = new Map<string, MergedShiftSegment[]>();
        dateMap.forEach((doctorShifts, doctorId) => {
          const sorted = [...doctorShifts].sort((a, b) =>
            extractTimeHm(a.start_time).localeCompare(extractTimeHm(b.start_time))
          );
          const segments: MergedShiftSegment[] = [];
          sorted.forEach((shift) => {
            // 旧 /api/doctor-shifts は日付付き時刻文字列を返すため HH:mm を抽出する
            const startTime = extractTimeHm(shift.start_time);
            const endTime = extractTimeHm(shift.end_time);
            const last = segments[segments.length - 1];

            if (
              last &&
              startTime <= last.endTime &&
              last.shift.slot_type === shift.slot_type
            ) {
              // 隣接（終了=次の開始）だけでなく、時間帯が重なる行も1本に結合する
              // （例: 09-11, 10-12, 11-13 → 09:00~13:00）。HH:mm形式のため文字列比較で安全
              if (endTime > last.endTime) {
                last.endTime = endTime;
              }
              last.shiftCount += 1;
            } else {
              segments.push({
                key: `seg-${shift.id}`,
                shift,
                startTime,
                endTime,
                shiftCount: 1,
              });
            }
          });
          doctorSegments.set(doctorId, segments);
        });
        merged.set(dateStr, doctorSegments);
      });

      return merged;
    }, [shifts, dates]);

    // セグメント位置を計算
    const positions = useMemo(() => {
      const positionMap = new Map<string, { left: number; width: number }>();
      segmentsByDateAndDoctor.forEach((doctorMap) => {
        doctorMap.forEach((segments) => {
          segments.forEach((segment) => {
            positionMap.set(
              segment.key,
              calculatePosition(segment.startTime, segment.endTime)
            );
          });
        });
      });
      return positionMap;
    }, [segmentsByDateAndDoctor, calculatePosition]);

    /**
     * タイムラインダブルクリックハンドラー
     */
    const handleDoubleClick = useCallback(
      (dateString: string, event: React.MouseEvent<HTMLDivElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const totalWidth = rect.width;
        const percentX = clickX / totalWidth;

        // クリック位置から時間を計算（15分単位で丸める）
        const totalMinutes = (config.dayEndHour - config.dayStartHour) * 60;
        const minutesFromStart = Math.floor(percentX * totalMinutes);
        const roundedMinutes = Math.round(minutesFromStart / 15) * 15;
        const hour = config.dayStartHour + Math.floor(roundedMinutes / 60);
        const minute = roundedMinutes % 60;
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

        onDoubleClick(dateString, time);
      },
      [onDoubleClick, config]
    );

    return (
      <TimelineContainer>
        {/* 時間軸ヘッダー */}
        <TimelineHeader />

        {/* 日付ごとのシフトグループ */}
        {dates.map((date) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const dateLabel = format(date, 'M/d(E)', { locale: ja });
          const doctorMap = segmentsByDateAndDoctor.get(dateStr) || new Map<string, MergedShiftSegment[]>();
          const readOnly = isPastDate(date);
          const doctorEntries = Array.from(doctorMap.entries());

          // シフトがない日付の場合は空行を1つ表示
          if (doctorEntries.length === 0) {
            return (
              <DateGroup key={dateStr}>
                <DateLabelColumn>
                  <Typography variant="body2" noWrap title={dateLabel}>
                    {dateLabel}
                  </Typography>
                </DateLabelColumn>
                <DoctorRowsContainer>
                  <DoctorRow>
                    <ShiftArea
                      onDoubleClick={!readOnly ? (e) => handleDoubleClick(dateStr, e) : undefined}
                    />
                  </DoctorRow>
                </DoctorRowsContainer>
              </DateGroup>
            );
          }

          // 医師ごとに行を生成
          return (
            <DateGroup key={dateStr}>
              {/* 日付ラベル（全行分を結合） */}
              <DateLabelColumn>
                <Typography variant="body2" noWrap title={dateLabel}>
                  {dateLabel}
                </Typography>
              </DateLabelColumn>

              {/* 医師行コンテナ */}
              <DoctorRowsContainer>
                {doctorEntries.map(([doctorId, segments]) => (
                  <DoctorRow key={`${dateStr}-${doctorId}`}>
                    <ShiftArea
                      onDoubleClick={!readOnly ? (e) => handleDoubleClick(dateStr, e) : undefined}
                    >
                      {segments.map((segment) => {
                        const position = positions.get(segment.key);
                        if (!position) return null;

                        return (
                          <ShiftBar
                            key={segment.key}
                            shift={segment.shift}
                            position={position}
                            startTime={segment.startTime}
                            endTime={segment.endTime}
                            shiftCount={segment.shiftCount}
                            onClick={(clickedShift) =>
                              onShiftClick(clickedShift, {
                                startTime: segment.startTime,
                                endTime: segment.endTime,
                              })
                            }
                            readOnly={readOnly}
                          />
                        );
                      })}
                    </ShiftArea>
                  </DoctorRow>
                ))}
              </DoctorRowsContainer>
            </DateGroup>
          );
        })}

        {/* シフトがない場合（全日付通じてシフトがない） */}
        {shifts.length === 0 && dates.length > 0 && (
          <EmptyState>
            <Box>該当するシフトがありません</Box>
            <Box sx={{ fontSize: '0.875rem', mt: 1 }}>
              タイムラインをダブルクリックしてシフトを追加できます
            </Box>
          </EmptyState>
        )}
      </TimelineContainer>
    );
  }
);

TimelineView.displayName = 'TimelineView';

export { TimelineView };
